/**
 * ContentAccessGate — "Unlock with code" panel.
 *
 * Rendered inside a gated course/event/session page when the backend
 * signals `content_locked=true`. Accepts a code, POSTs to
 * `/api/content-codes/redeem`, and calls `onUnlocked()` on success so
 * the parent can refetch and reveal the content.
 *
 * Props:
 *   targetLabel: string   — human-readable name of the gated resource
 *   onUnlocked:  () => void
 */
import React, { useContext, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../App';
import { Button } from '../ui/button';
import { Lock, KeyRound, Loader2 } from 'lucide-react';

const ContentAccessGate = ({ targetLabel = 'this content', onUnlocked }) => {
  const { API } = useContext(AuthContext);
  const [code, setCode] = useState('');
  const [redeeming, setRedeeming] = useState(false);

  const submit = async (e) => {
    e && e.preventDefault();
    const c = (code || '').trim().toUpperCase();
    if (!c) {
      toast.error('Enter your access code.');
      return;
    }
    setRedeeming(true);
    try {
      const r = await axios.post(`${API}/content-codes/redeem`, { code: c });
      if (r.data?.already_owned) {
        toast.info('You already have access — refreshing.');
      } else {
        toast.success(`Unlocked: ${r.data?.target_label || targetLabel}`);
      }
      onUnlocked && onUnlocked();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not redeem code.');
    } finally {
      setRedeeming(false);
    }
  };

  return (
    <div
      className="rounded-2xl border-2 border-primary/30 bg-primary/5 p-6 md:p-8 text-center"
      data-testid="content-access-gate"
    >
      <div className="mx-auto h-12 w-12 rounded-2xl bg-primary/15 text-primary flex items-center justify-center">
        <Lock className="w-6 h-6" />
      </div>
      <h3 className="mt-3 font-heading text-xl font-extrabold text-foreground">
        Members-only: {targetLabel}
      </h3>
      <p className="mt-1 text-sm text-muted-foreground">
        This resource is unlocked by an access code. Enter the code you received
        from your event organizer, study-session host, or instructor.
      </p>

      <form onSubmit={submit} className="mt-5 max-w-sm mx-auto flex flex-col sm:flex-row items-stretch gap-2">
        <div className="relative flex-1">
          <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="XX-XXXX-XXXX-XXXX"
            className="w-full h-11 pl-9 pr-3 rounded-full border border-border bg-background font-mono uppercase tracking-widest text-sm placeholder:text-muted-foreground focus:outline-none focus:border-primary/60"
            data-testid="content-code-input"
            autoFocus
          />
        </div>
        <Button
          type="submit"
          disabled={redeeming}
          className="h-11 rounded-full font-bold"
          data-testid="content-code-submit-btn"
        >
          {redeeming ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Unlocking…</>) : 'Unlock'}
        </Button>
      </form>

      <p className="mt-4 text-xs text-muted-foreground">
        Don&rsquo;t have a code? Ask the organizer who invited you, or{' '}
        <a href="/messages" className="underline">DM support</a>.
      </p>
    </div>
  );
};

export default ContentAccessGate;
